/* eslint-disable react/prop-types */
/**
 * Validated autocomplete field to be used in a <ValidatedForm>. Should be used
 * as <ValidatedForm.Autocomplete>, not imported directly.
 *
 * Usage:
 * ```js
 * <ValidatedForm ...>
 *   <ValidatedForm.Autocomplete
 *     name="company"
 *     items={[...]}
 *     validate={[required, number]}
 *     warn={max(10)} />
 * </ValidatedForm>
 * ```
 */

import {
  change,
  Field,
} from 'redux-form';
import { useDispatch } from 'react-redux';
import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { debounce } from 'lodash';
import tooltipSchema from '../../../js/schemas/tooltip';
import Alert from '../../atoms/Alert';
import Autocomplete from '../../atoms/Autocomplete';
import TextInput from '../../atoms/TextInput';

const ValidatedFormAddressAutocomplete = ({
  className,
  name,
  validate,
  warn,
  compact,
  suffix,
  validateOnRegister,
  onChange,
  onSelect,
  tooltip,
  data,
  ...rest
}) => (
  <Field
    name={data.name}
    label={data.label}
    className={className}
    // eslint-disable-next-line no-use-before-define
    component={ValidatedAutocomplete}
    validate={data.validate}
    warn={warn}
    onSelect={onSelect}
    compact={compact}
    suffix={suffix}
    hasValidateFunction={!!validate}
    validateOnRegister={validateOnRegister}
    tooltip={tooltip}
    {...rest}
    {...data}
  />
);

ValidatedFormAddressAutocomplete.propTypes = {
  className: PropTypes.string,
  /** Used as key in redux. */
  name: PropTypes.string,
  /** Validation function(s). Takes a value and returns an error string or undefined. */
  validate: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.func),
  ]),
  /** Validation function(s). Takes a value and returns a warning string or undefined. */
  warn: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.func),
  ]),
  items: PropTypes.arrayOf(PropTypes.shape()),
  compact: PropTypes.bool,
  suffix: PropTypes.string,
  validateOnRegister: PropTypes.bool,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  tooltip: tooltipSchema,
};

ValidatedFormAddressAutocomplete.defaultProps = {
  className: '',
  validate: null,
  warn: () => {},
  items: [],
  compact: false,
  suffix: '',
  validateOnRegister: false,
  onChange: () => {},
  onSelect: () => {},
  tooltip: '',
  name: '',
};

export default ValidatedFormAddressAutocomplete;

const ValidatedAutocomplete = ({
  className,
  wrapperClassName,
  input,
  meta: {
    valid,
    touched,
    error,
    warning,
  },
  label,
  suffix,
  compact,
  hasValidateFunction,
  validateOnRegister,
  tooltip,
  onSelect,
  getItemValue,
  showErrors,
  ...rest
}) => {
  const inputId = `validated-form-autocomplete--${input.name}`;
  const shouldValidate = valid || touched || validateOnRegister;
  const [items, setItems] = useState([]);
  const changeField = (...args) => change('application', ...args);
  const dispatch = useDispatch();

  const askGoogle = debounce((v) => { // eslint-disable-line no-unused-vars
    const service = new google.maps.places.AutocompleteService(); // eslint-disable-line no-undef
    service.getPlacePredictions({
      input: v,
      componentRestrictions: {
        country: ['se'],
      },
      language: 'sv',
      fields: ['address_components'],
      types: ['route'],
    }, (results, status) => {
      if (status !== 'OK') {
        return;
      }

      setItems(results.map((result) => ({
        name: result.description,
        data: result,
      })));
    });
  }, 500);

  const select = (...args) => {
    if (onSelect) {
      onSelect(...args);
    }
    if (input.onChange) {
      input.onChange(...args);
    }

    const [_, item] = args; // eslint-disable-line no-unused-vars
    const map = new google.maps.Map(document.getElementsByTagName('body')); // eslint-disable-line no-undef
    const placesService = new google.maps.places.PlacesService(map); // eslint-disable-line no-undef

    placesService.getDetails({
      placeId: item.data.place_id,
      fields: ['address_components', 'geometry'],
    }, async (results, status) => {
      if (status !== 'OK') {
        return;
      }

      const lat = results?.geometry?.location?.lat();
      const lng = results?.geometry?.location?.lng();

      const streetNumber = results?.address_components?.find((component) => {
        const [type] = component.types;
        return type === 'street_number';
      })?.long_name;

      const streetName = results?.address_components?.find((component) => {
        const [type] = component.types;
        return type === 'route';
      })?.long_name;

      const zipCode = results?.address_components?.find((component) => {
        const [type] = component.types;
        return type === 'postal_code';
      })?.long_name;

      const cityName = results?.address_components?.find((component) => {
        const [type] = component.types;
        return type === 'postal_town';
      })?.long_name;

      if (streetName && streetNumber) {
        dispatch(changeField('property.address', `${streetName} ${streetNumber}`));
      } else if (streetName) {
        dispatch(changeField('property.address', `${streetName}`));
      }

      dispatch(changeField('property.zipCode', zipCode));
      dispatch(changeField('property.cityName', cityName));

      const geocoder = new google.maps.Geocoder(); // eslint-disable-line no-undef
      const response = await geocoder.geocode(
        {
          location: {
            lat,
            lng,
          },
          language: 'sv',
        },
      );
      const municipalityName = response.results.find((result) => {
        const [type] = result.types;
        return type === 'administrative_area_level_2';
      }).address_components.find((component) => {
        const [type] = component.types;
        return type === 'administrative_area_level_2';
      }).long_name;

      dispatch(changeField('property.municipalityName', municipalityName));
    });
  };

  return (
    <div className={wrapperClassName} data-formfield={input.name}>
      <Autocomplete
        {...input}
        {...rest}
        items={items}
        getItemValue={(item) => item.name}
        onChange={(e, v) => {
          input.onChange(e, v);
          // askGoogle(v);
        }}
        onSelect={select}
        inputProps={{
          onFocus: input.onFocus,
          onBlur: input.onBlur,
        }}
        renderInput={({ ref, ...inputProps }) => (
          <TextInput
            inputRef={ref}
            {...rest}
            {...inputProps}
            id={inputId}
            name={input.name}
            label={label}
            suffix={suffix}
            compact={compact}
            validated={hasValidateFunction}
            valid={shouldValidate ? valid : null}
            className={className}
            tooltip={tooltip}
          />
        )}
      />
      {(shouldValidate || showErrors) && (
        (error && <Alert display={error} />)
        || (warning && <Alert type="warning" display={warning} />)
      )}
    </div>
  );
};

export { ValidatedAutocomplete };
